import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { logout } from '@store/features/userSlice';
import { clearAuthCookies, getAuthToken } from '@utils/cookie';
import { getApiBaseUrl } from '@utils/apiBaseUrl';
import { applyRefererHeader, getRequestReferrer, REQUEST_REFERRER_POLICY } from '@utils/requestReferrer';

const baseUrl = getApiBaseUrl();

const GUEST_URL = '/u/user/guest';
const VERIFY_URL = '/u/user/verify';
const LOGIN_URL = '/u/user/login';
const REGISTER_URL = '/u/user/register';
const TOKEN_SHARE_CONFIRM_URL = '/u/user/token/share/confirm';
const NO_TOKEN_URLS = new Set([
  '/c/announcement/get',
  '/c/traffic/visit',
  GUEST_URL,
  VERIFY_URL,
  LOGIN_URL,
  REGISTER_URL,
  TOKEN_SHARE_CONFIRM_URL,
]);

/** 后端统一返回 BaseResult<T>，需解包 data；success=false 时抛出业务错误 */
function handleNotLogin(api: Parameters<BaseQueryFn>[1]): void {
  clearAuthCookies();
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem('token');
  }
  api.dispatch(logout());
  if (typeof window !== 'undefined') {
    const to = window.location.pathname.startsWith('/admin') ? '/admin/login' : '/login';
    if (window.location.pathname !== to) {
      window.location.href = to;
    }
  }
}

function transformBaseResult<T>(response: unknown, api: Parameters<BaseQueryFn>[1], checkAuth: boolean): T {
  const parsed = response as { success?: boolean; errorMessage?: string; errorCode?: string; data?: T };
  if (parsed && parsed.success === false) {
    if (checkAuth && (parsed.errorCode === 'NOT_LOGIN' || parsed.errorCode === 'TOKEN_INVALID')) {
      handleNotLogin(api);
    }
    throw new Error(parsed.errorMessage || parsed.errorCode || 'Request failed');
  }
  return parsed?.data as T;
}

function isNotLoginPayload(response: unknown): boolean {
  const parsed = response as { errorCode?: string; errorMessage?: string; success?: boolean } | null;
  if (!parsed || typeof parsed !== 'object') return false;
  return parsed.errorCode === 'NOT_LOGIN' || parsed.errorCode === 'TOKEN_INVALID' || parsed.errorMessage === 'NOT_LOGIN';
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl,
  credentials: 'include',
  referrerPolicy: REQUEST_REFERRER_POLICY,
  prepareHeaders: (headers) => {
    headers.set('Content-Type', 'application/json');
    return applyRefererHeader(headers);
  },
});

function withFetchReferrer(args: string | FetchArgs): FetchArgs {
  return typeof args === 'string'
    ? { url: args, referrer: getRequestReferrer(), referrerPolicy: REQUEST_REFERRER_POLICY }
    : { ...args, referrer: getRequestReferrer(), referrerPolicy: REQUEST_REFERRER_POLICY };
}

/** 除 guest 外，所有请求的 body 中注入 token */
const baseQueryWithToken: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  const url = typeof args === 'string' ? args : args.url;
  const requestToken = getAuthToken();
  const checkAuth = url !== '/c/traffic/visit' && url !== '/c/announcement/get';
  const unwrapResult = (
    result: Awaited<ReturnType<typeof rawBaseQuery>>
  ): { data: unknown } | { error: FetchBaseQueryError } => {
    // A late response from the previous account must not sign out or populate the next account.
    if (requestToken !== getAuthToken()) {
      return { error: { status: 'CUSTOM_ERROR', error: 'Identity changed' } };
    }
    if ('error' in result) {
      if (checkAuth && isNotLoginPayload(result.error?.data)) {
        handleNotLogin(api);
      }
      return {
        error: result.error ?? { status: 'CUSTOM_ERROR', error: 'Network request failed' },
      };
    }
    try {
      return { data: transformBaseResult(result.data, api, checkAuth) };
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Request failed';
      return {
        error: {
          status: 'CUSTOM_ERROR',
          error: message,
          data: result.data,
        },
      };
    }
  };

  if (!NO_TOKEN_URLS.has(url)) {
    const body = typeof args === 'object' && args != null && 'body' in args && args.body && typeof args.body === 'object'
      ? { ...args.body, token: getAuthToken() ?? '' }
      : { token: getAuthToken() ?? '' };
    const newArgs: FetchArgs = withFetchReferrer(
      typeof args === 'string' ? { url: args, method: 'POST', body } : { ...args, body }
    );
    const result = await rawBaseQuery(newArgs, api, extraOptions);
    return unwrapResult(result);
  }
  const result = await rawBaseQuery(withFetchReferrer(args), api, extraOptions);
  return unwrapResult(result);
};

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithToken,
  tagTypes: ['Category', 'Product', 'Order', 'User', 'Cart', 'Comment', 'Coupon', 'TrafficLink', 'Announcement', 'ConsultationList'],
  endpoints: () => ({}),
});
